/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import { useState } from 'react';
import { Form, FormGroup, FormText, Input, Label } from 'reactstrap';
import UTRSearch from '../search/UTRSearch';
import { UtrValueType, TableColumnType } from '../../types/universalTracker';
import { ValueListSelection } from '@g17eco/molecules/value-list-selection';
import { MetricSubmitData, ShowAsTypes } from '../../types/custom-report';
import { QUESTION } from '@constants/terminology';
import { UTRSuggestion } from '@g17eco/types/search';

interface CreateMetricFormProps {
  initiativeId: string;
  handleUpdate: (data: MetricSubmitData | undefined) => void;
  primaryUtrTypes?: UtrValueType[];
  primaryUtrColumnTypes?: TableColumnType[];
  secondaryUtrTypes?: UtrValueType[];
  secondaryUtrColumnTypes?: TableColumnType[];
  instructions?: string | null;
  showAsTypes?: ShowAsTypes[];
}

const getInitialMetricSubmitData = (data: Partial<MetricSubmitData>): MetricSubmitData => ({
  name: '',
  showAs: data.showAs ?? 'total',
  percentageOf: 'count',
  primaryUtrCode: '',
  primaryValueListCode: '',
  primaryColumnCode: '',
  secondaryUtrCode: '',
  secondaryValueListCode: '',
  secondaryColumnCode: '',
});

interface CreateMetricSubmitData extends MetricSubmitData {
  primaryUtr?: UTRSuggestion;
  secondaryUtr?: UTRSuggestion;
}

const CreateMetricForm = (props: CreateMetricFormProps) => {
  const {
    initiativeId,
    primaryUtrTypes = [UtrValueType.ValueList, UtrValueType.ValueListMulti, UtrValueType.Table, UtrValueType.Number, UtrValueType.Percentage],
    primaryUtrColumnTypes = [TableColumnType.ValueList, TableColumnType.Text, TableColumnType.ValueListMulti, TableColumnType.Number],
    secondaryUtrTypes = [UtrValueType.Number, UtrValueType.Table],
    secondaryUtrColumnTypes = [TableColumnType.Number],
    handleUpdate,
    instructions = 'Select the metric above that you would like to use in your custom target',
    showAsTypes = ['total', 'percentage']
  } = props;

  const [metricSubmitData, setMetricSubmitData] = useState<CreateMetricSubmitData>(getInitialMetricSubmitData({
    showAs: showAsTypes[0]
  }));

  const handlePrimaryUTRSearch = (searchStr: string, currentSuggestion?: UTRSuggestion) => {
    setData({
      primaryUtr: currentSuggestion,
      primaryUtrCode: currentSuggestion?.code,
      primaryValueListCode: currentSuggestion?.valueListOptions?.options[0].code ?? ''
    });
  }

  const handleSecondaryUTRSearch = (searchStr: string, currentSuggestion?: UTRSuggestion) => {
    setData({
      secondaryUtr: currentSuggestion,
      secondaryUtrCode: currentSuggestion?.code,
      secondaryValueListCode: currentSuggestion?.valueListOptions?.options[0].code ?? ''
    });
  }

  const setData = (partial: Partial<CreateMetricSubmitData>) => {
    const newState = {
      ...metricSubmitData,
      ...partial
    };

    if (newState.showAs !== 'percentage') {
      newState.percentageOf = 'count';
    }

    if (newState.percentageOf === 'count') {
      // This is a 'reset' action that needs to clear secondary Utr or things can get confused
      newState.secondaryUtr = undefined;
      newState.secondaryUtrCode = '';
      newState.secondaryValueListCode = '';
    }

    setMetricSubmitData(newState);
    pushUpdate(newState);
  }

  const pushUpdate = (submitData: CreateMetricSubmitData) => {
    const isValidPrimaryUtr = submitData.primaryUtr && (submitData.primaryUtr.valueType !== UtrValueType.Table || submitData.primaryColumnCode);
    const isValidBasics = submitData.name && isValidPrimaryUtr;

    const isPercentage = submitData.showAs === 'percentage';
    const isValidPercentageOfAnotherMetric = isPercentage && submitData.percentageOf === 'metric' && submitData.secondaryUtr;
    const isValidPercentageOfChildren = isPercentage && submitData.percentageOf === 'count';
    const isValidPercentage = isValidBasics && isPercentage && (isValidPercentageOfAnotherMetric || isValidPercentageOfChildren)

    const isValidTotal = isValidBasics && !isPercentage;

    const canSubmit = isValidTotal || isValidPercentage;

    if (!canSubmit || !submitData.primaryUtr) {
      handleUpdate(undefined);
      return;
    }

    handleUpdate(submitData);
  };

  const formulaOptions: { code: ShowAsTypes, name: string }[] = [
    {
      code: 'total',
      name: 'Total',
    },
    {
      code: 'text',
      name: 'Text',
    },
    {
      code: 'percentage',
      name: 'Percentage',
    }
  ];

  const emptyMessage = instructions ? <div className='my-4 p-3 background-ThemeNeutralsLight border border-ThemeTextPlaceholder'>{instructions}</div> : null;

  return (
    <Form>
      <FormGroup>
        <Label for='name'>Title of new metric</Label>
        <FormText className='float-end'>({metricSubmitData.name.length} of 75 characters)</FormText>
        <Input maxLength={75} name='name' placeholder='Type the title here' onChange={(e) => setData({ name: e.target.value })}></Input>
      </FormGroup>
      <FormGroup className='mt-3'>
        <Label for='utrCode'>Select your metric</Label>
        <FormText className='float-end'>(only some {QUESTION.PLURAL} supported)</FormText>
        <UTRSearch
          placeholder='Search'
          handleValueChange={handlePrimaryUTRSearch}
          inputClassName='form-control'
          initiativeId={initiativeId}
          filter={{
            valueType: primaryUtrTypes
          }}
        />
      </FormGroup>
      {
        !metricSubmitData.primaryUtr ?
          emptyMessage
          :
          <>
            <ValueListSelection
              utr={metricSubmitData.primaryUtr}
              setValueListCode={(value) => setData({ primaryValueListCode: value })}
              selectedValueListCode={metricSubmitData.primaryValueListCode}
              setColumnCode={(value) => setData({ primaryColumnCode: value })}
              selectedColumnCode={metricSubmitData.primaryColumnCode}
              allowedUtrValueTypes={primaryUtrTypes}
              allowedColumnValueTypes={primaryUtrColumnTypes}
            />

            <FormGroup tag='fieldset' className='mt-3'>
              <Label for='formula'>Show as:</Label>
              <div className='row mx-1'>
                {formulaOptions
                  .filter(f => showAsTypes.includes(f.code))
                  .map((vl) => (
                    <FormGroup className='col-4' key={`formula-radio${vl.code}`} check>
                      <Input
                        type='radio'
                        name='formula-radio'
                        defaultChecked={vl.code === metricSubmitData.showAs} value={vl.code}
                        onClick={() => setData({ showAs: vl.code })} />
                      <Label check>
                        {vl.name}
                      </Label>
                    </FormGroup>
                  ))}
              </div>
            </FormGroup>
            <FormGroup tag='fieldset' className='mt-3'>
              {metricSubmitData.showAs === 'percentage' ?
                <>
                  <Label for='percentageOf'>Percentage of:</Label>
                  <div className='mx-1'>
                    <FormGroup check>
                      <Input type='radio' name='percentageOf' defaultChecked={metricSubmitData.percentageOf === 'count'}
                        value='count' onClick={() => setData({ percentageOf: 'count' })} />
                      <Label check>
                        Number of business units
                      </Label>
                    </FormGroup>
                    <FormGroup check>
                      <Input type='radio' name='percentageOf' defaultChecked={metricSubmitData.percentageOf === 'metric'}
                        value='metric' onClick={() => setData({ percentageOf: 'metric' })} />
                      <Label check>
                        Select another metric:
                      </Label>
                    </FormGroup>
                  </div>
                  {metricSubmitData.percentageOf === 'metric' ?
                    <>
                      <div className='mx-1'>
                        <div className='px-0 pt-2'>
                          <div className='small text-ThemeTextPlaceholder text-right'>(number {QUESTION.PLURAL} only)</div>
                          <UTRSearch
                            placeholder='Search'
                            handleValueChange={handleSecondaryUTRSearch}
                            inputClassName='form-control'
                            initiativeId={initiativeId}
                            filter={{
                              valueType: secondaryUtrTypes
                            }}
                          />
                        </div>
                      </div>
                      {metricSubmitData.secondaryUtr ? <ValueListSelection
                        utr={metricSubmitData.secondaryUtr}
                        setValueListCode={(value) => setData({ secondaryValueListCode: value })}
                        selectedValueListCode={metricSubmitData.secondaryValueListCode}
                        setColumnCode={(value) => setData({ secondaryColumnCode: value })}
                        selectedColumnCode={metricSubmitData.secondaryColumnCode}
                        allowedUtrValueTypes={secondaryUtrTypes}
                        allowedColumnValueTypes={secondaryUtrColumnTypes}
                      />
                        : <></>}
                    </>
                    : <></>
                  }
                </>
                :
                <></>
              }
            </FormGroup>
          </>
      }
    </Form>
  );
}

export default CreateMetricForm;
